/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "@/constants/site";

const SECTIONS = [
    { title: "Nonprofit Start-Up", text: "From founding members to filing — articles, bylaws, EIN, state registrations, and your federal 501(c)(3) application.", slug: "nonprofit-planning" },
    { title: "501(c)(3) Planning Support", text: "Form 1023 / 1023-EZ readiness, narrative support, budget formatting, and compliance roadmap.", slug: "nonprofit-planning" },
    { title: "Program Development", text: "Translate your mission into measurable, fundable programs with logic models and outcome frameworks.", slug: "behavioral-health-strategy" },
    { title: "Grant Writing", text: "Prospect research, LOIs, full proposals, and reporting infrastructure that funders respect.", slug: "grant-writing" },
    { title: "Fundraising Strategy", text: "Annual fund, major gifts, events, sponsorships, and a development calendar your board can run.", slug: "grant-writing" },
    { title: "Board Development", text: "Recruitment, governance training, board matrices, and committee structures that scale.", slug: "board-development" },
    { title: "Community Partnerships", text: "Open the doors to faith communities, schools, civic groups, and city/county agencies.", slug: "organizational-development" },
    { title: "Workforce Development", text: "Programs that connect participants to employment, training, and long-term self-sufficiency.", slug: "organizational-development" },
    { title: "Recovery & Reentry Program Strategy", text: "Sober living, peer support, employment pathways — informed by The Mend House model.", slug: "behavioral-health-strategy" },
    { title: "Behavioral Health Program Planning", text: "Service line design, compliance pathways, and outcomes frameworks for treatment providers.", slug: "behavioral-health-strategy" },
];

export default function Nonprofit() {
    return (
        <div data-testid="nonprofit-page">
            <section className="relative bg-[#0A192F] text-white py-28 overflow-hidden">
                <img
                    src={ASSETS.communityVolunteer}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover object-center opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/70" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Nonprofit & Community Development</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-4xl">
                        Building the kind of nonprofits
                        <br />
                        <span className="italic text-[#D4AF37]">funders actually fund.</span>
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        Grounded by two decades of community work — including
                        founding The HopeWell Foundation and co-founding The
                        Mend House — we help mission-driven leaders build the
                        governance, programs, and revenue to last.
                    </p>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {SECTIONS.map((s, idx) => (
                            <Link
                                key={s.title}
                                to={`/services/${s.slug}`}
                                data-testid={`nonprofit-section-${idx}`}
                                className="bg-white p-8 hover:bg-slate-50 transition-colors group block"
                            >
                                <span className="num-tag">
                                    {String(idx + 1).padStart(2, "0")}
                                </span>
                                <h3 className="font-serif text-xl font-bold mt-3 text-[#0A192F] group-hover:text-[#1E3A8A] transition-colors">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                    {s.text}
                                </p>
                                <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#0A192F] group-hover:text-[#D4AF37] transition-colors">
                                    Learn more <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-24">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <span className="overline">Credibility</span>
                        <h2 className="font-serif text-4xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Built with hands that have done the work.
                        </h2>
                        <p className="mt-6 text-slate-700 leading-relaxed">
                            The HopeWell Foundation has provided resources,
                            recovery services, and workforce development across
                            East Tennessee. The Mend House has served{" "}
                            <strong>700+ men since 2015</strong> through sober
                            living, accountability, employment, and recovery
                            support. Hopewell Helping Hands creates employment
                            pathways and self-sufficiency for individuals
                            rebuilding their lives.
                        </p>
                        <p className="mt-4 text-slate-700 leading-relaxed">
                            When we help you stand up a nonprofit, we're
                            handing you what we built — not just what we
                            studied.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="exec-card p-8 bg-[#0A192F] text-white border-[#0A192F] text-center">
                            <div className="font-serif text-5xl sm:text-6xl font-bold text-[#D4AF37] leading-none">
                                700+
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] mt-3 text-slate-300">
                                Men served · The Mend House · Since 2015
                            </div>
                            <div className="mt-8 font-serif text-3xl sm:text-4xl font-bold text-[#D4AF37] leading-none">
                                1000s
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] mt-3 text-slate-300">
                                Lives impacted across counseling & community programs
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-[#0A192F] text-white">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
                        Build a nonprofit that lasts.
                    </h3>
                    <Link
                        to="/contact?service=Nonprofit Planning and Development"
                        data-testid="nonprofit-cta"
                        className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest"
                    >
                        Request a Consultation <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
